import {
	ATTRIBUTES,
	ON_CHANGE_DEBOUNCE_TIMER,
	PUB_SUB_EVENTS,
	SELECTORS,
} from '@/scripts/core/global'
import { fetchConfig } from '@/scripts/core/global'
import { TsDOM as q, debounce } from '@/scripts/core/TsDOM'
import { publish, PubSubEvent, subscribe } from '@/scripts/core/global'
import { type ShopifySectionRenderingSchema } from '@/scripts/types/theme'
import { type CartDrawer } from '@/scripts/cart/cart-drawer'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	getCart,
	getUpdateInstructions,
	renderResponseToCartDrawer,
	updateProgressBar,
} from '@/scripts/core/cart-functions'
import {
	getCartState,
	applyQuantityChange as stateApplyQtyChange,
	applyRemove as stateApplyRemove,
} from '@/scripts/cart/cart-state'
import {
	setCartLinePricesLoading,
	updateOptimisticTotals,
} from '@/scripts/cart/optimistic-cart'
import { releaseLock } from '@/scripts/cart/optimistic-lock'
import {
	cartDebug,
	summarizeCartItems,
	summarizeDomCartItems,
	summarizeInstructions,
} from '@/scripts/cart/optimistic-debug'

export class CartItems extends UcoastEl {
	// static
	static htmlSelector = 'cart-items'
	static selectors = {
		element: 'cart-items',
		itemLink: '[data-uc-cart-item-name]',
		lineItemStatus: '[data-uc-cart-status]',
		errors: '[data-uc-cart-errors]',
		liveRegionText: '#CartPage-LiveRegionText',
		main: '#main-cart-items',
		cartDrawerInner: '[data-uc-cart-drawer-inner]',
		cartDrawerInnerEmpty: '[data-uc-cart-drawer-inner-empty]',
		item: '[data-uc-cart-item]',
		itemErrorText: '[data-uc-cart-item-error-text]',
		footer: '#CartPage-Footer',
		loadingOverlay: '[data-uc-loading-overlay]',
		// the following selectors are partial - they will be concatenated with the line ID
		line: '#CartPage-Item', // ex: `${this.instanceSelectors.line}-${line}`
		lineQuantity: '#CartPage-LineItemQuantity', // ex: `${this.instanceSelectors.lineQuantity}-${line}`
		lineError: '#CartPage-LineItemError', // ex: `${this.instanceSelectors.lineError}-${line}`
	}
	// TODO: refactor to global attributes selector
	static attributes = {
		disabled: 'data-uc-disabled',
	}

	// instance specific property types
	instanceSelectors = CartItems.selectors
	lineItemStatusElement: HTMLElement
	cartUpdateUnsubscriber?: () => void = undefined

	// init
	constructor() {
		super()
		this.setInstanceSelectors()
		this.lineItemStatusElement = q.rs(this.instanceSelectors.lineItemStatus)

		const debouncedOnChange = debounce((event: Event) => {
			this.onChange(event)
		}, ON_CHANGE_DEBOUNCE_TIMER)

		this.addEventListener('change', debouncedOnChange.bind(this))
	}

	// have to instantiate selectors here so they can be different for CartDrawerItems
	setInstanceSelectors() {
		this.instanceSelectors = CartItems.selectors
	}

	// from here on is default Dawn CartItems -> typescript
	override connectedCallback() {
		this.cartUpdateUnsubscriber = subscribe(
			PUB_SUB_EVENTS.cartUpdate,
			(pubSubEvent: PubSubEvent) => {
				if (
					pubSubEvent &&
					'source' in pubSubEvent &&
					pubSubEvent.source === 'cart-items'
				) {
					return
				}
				this.onCartUpdate()
			}
		)
	}

	override disconnectedCallback() {
		if (this.cartUpdateUnsubscriber) {
			this.cartUpdateUnsubscriber()
		}
	}

	onChange(event: Event) {
		const target = q.rt<Event, HTMLInputElement>(event)
		const targetIndex = q.ra(target, 'data-index')
		const buttonName =
			event instanceof CustomEvent ? event.detail?.buttonName : undefined
		this.updateQuantity(targetIndex, target.value, buttonName)
	}

	onCartUpdate() {
		// Skip when an optimistic add is in progress —
		// renderContents will handle the section swap. Firing this fetch
		// would compete for Shopify's session-locked cart endpoint.
		if (document.querySelector('[data-optimistic-item]')) return

		fetch(`${window.routes.cart_url}?section_id=main-cart-items`)
			.then((response) => response.text())
			.then((responseText) => {
				const html = new DOMParser().parseFromString(
					responseText,
					'text/html'
				)
				// reference CartItems selectors here since this returns then inner HTML I guess
				const source = q.rs(
					CartItems.selectors.element,
					html.documentElement
				)
				this.innerHTML = source.innerHTML
			})
			.catch((e) => {
				console.error(e)
			})
	}

	getSectionsToRender(): ShopifySectionRenderingSchema[] {
		return [
			{
				id: 'main-cart-items',
				section: q.ra(q.rs(this.instanceSelectors.main), 'data-id'),
				selector: '.js-contents',
			},
			{
				id: 'CartIconBubble',
				section: 'cart-icon-bubble',
				selector: SELECTORS.cartLink,
			},
			{
				id: 'CartPage-LiveRegionText',
				section: 'cart-live-region-text',
				selector: '.shopify-section',
			},
			{
				id: 'CartPage-Footer',
				section: q.ra(q.rs(this.instanceSelectors.footer), 'data-id'),
				selector: '.js-contents',
			},
			{
				id: 'CartDrawer',
				section: 'cart-update-instructions',
				selector: '[data-cart-update-instructions]',
			},
		]
	}

	updateQuantity(line: string, quantity: string, name?: string) {
		const lineIndex = parseInt(line)
		const newQty = parseInt(quantity)
		const isDrawer = this.tagName.toLowerCase() === 'cart-drawer-items'
		const cartDrawerWrapper = q.os<CartDrawer>('cart-drawer')
		cartDebug('cart-items:updateQuantity:start', {
			line,
			quantity,
			lineIndex,
			newQty,
			isDrawer,
			domItems: summarizeDomCartItems(this),
		})

		// ── Optimistic UI (drawer only) ─────────────────────────────
		if (isDrawer && cartDrawerWrapper && getCartState().items.length > 0) {
			if (newQty === 0) {
				// Optimistic remove: collapse line + cascading items
				const { state, removedIndices, removedKeys } =
					stateApplyRemove(lineIndex)
				cartDebug('cart-items:optimistic-remove:state', {
					lineIndex,
					removedIndices,
					removedKeys,
					stateItems: summarizeCartItems(state.items),
				})
				for (const idx of removedIndices) {
					const lineItem = document.querySelector(
						`${this.instanceSelectors.line}-${idx}`
					)
					if (lineItem instanceof HTMLElement) {
						lineItem.style.maxHeight = `${lineItem.scrollHeight}px`
						requestAnimationFrame(() => {
							lineItem.classList.add('cart-item--removing')
						})
					}
				}
				updateOptimisticTotals(state.totals)

				// Capture item data before removal for Segment tracking
				void this.captureAndTrackRemoval(lineIndex)

				// Acquire lock and fire server request in background
				const lockToken = (
					cartDrawerWrapper as CartDrawer
				).acquireOptimisticLock('remove')
				cartDebug('cart-items:optimistic-remove:lock-acquired', {
					lockToken,
				})

				// When cascading (removing product + its membership/GWP), use
				// /cart/update.js with all keys zeroed in one request instead of
				// /cart/change.js which only handles a single line.
				const sections = this.getSectionsToRender().map(
					(s) => s.section
				)
				const useBatchRemove = removedKeys.length > 1

				const url = useBatchRemove
					? `${window.routes.cart_update_url}`
					: `${window.routes.cart_change_url}`

				const body = useBatchRemove
					? JSON.stringify({
							updates: Object.fromEntries(
								removedKeys.map((k) => [k, 0])
							),
							sections,
							sections_url: window.routes.cart_url,
						})
					: JSON.stringify({
							line: lineIndex,
							quantity: 0,
							sections,
							sections_url: window.routes.cart_url,
						})

				cartDebug('cart-items:optimistic-remove:request', {
					url,
					useBatchRemove,
					body: JSON.parse(body),
				})

				fetch(url, { ...fetchConfig(), body })
					.then((response) => response.text())
					.then((text) => {
						const parsedState = JSON.parse(text)
						cartDebug('cart-items:optimistic-remove:response', {
							hasErrors: Boolean(parsedState.errors),
							itemCount: parsedState.item_count,
							items: summarizeCartItems(parsedState.items ?? []),
						})
						if (parsedState.errors) {
							releaseLock(lockToken)
							void (
								cartDrawerWrapper as CartDrawer
							).forceRefreshFromServer()
							return
						}
						;(cartDrawerWrapper as CartDrawer).handleServerResponse(
							parsedState,
							lockToken
						)
						publish(PUB_SUB_EVENTS.cartUpdate, {
							source: 'cart-items',
						})
					})
					.catch(() => {
						cartDebug('cart-items:optimistic-remove:exception')
						releaseLock(lockToken)
						void (
							cartDrawerWrapper as CartDrawer
						).forceRefreshFromServer()
					})
				return
			}

			// Optimistic quantity change: update DOM instantly
			const qtyState = stateApplyQtyChange(lineIndex, newQty)
			cartDebug('cart-items:optimistic-qty:state', {
				lineIndex,
				newQty,
				stateItems: summarizeCartItems(qtyState.items),
			})
			const qtyInput = q.os<HTMLInputElement>(
				`${this.instanceSelectors.lineQuantity}-${line}`
			)
			if (qtyInput) qtyInput.value = String(newQty)

			const stateItem = qtyState.items[lineIndex - 1]
			if (stateItem) {
				const lineItem = document.querySelector(
					`${this.instanceSelectors.line}-${line}`
				)
				if (lineItem) {
					setCartLinePricesLoading(lineItem)
				}
			}
			updateOptimisticTotals(qtyState.totals)

			// Acquire lock and fire server request in background
			const lockToken = (
				cartDrawerWrapper as CartDrawer
			).acquireOptimisticLock('qty-change')
			cartDebug('cart-items:optimistic-qty:lock-acquired', {
				lockToken,
			})

			const body = JSON.stringify({
				line: lineIndex,
				quantity: newQty,
				sections: this.getSectionsToRender().map(
					(section) => section.section
				),
				sections_url: window.routes.cart_url,
			})

			fetch(`${window.routes.cart_change_url}`, {
				...fetchConfig(),
				...{ body },
			})
				.then((response) => response.text())
				.then((state) => {
					const parsedState = JSON.parse(state)
					cartDebug('cart-items:optimistic-qty:response', {
						hasErrors: Boolean(parsedState.errors),
						itemCount: parsedState.item_count,
						items: summarizeCartItems(parsedState.items ?? []),
					})
					if (parsedState.errors) {
						releaseLock(lockToken)
						void (
							cartDrawerWrapper as CartDrawer
						).forceRefreshFromServer()
						return
					}
					;(cartDrawerWrapper as CartDrawer).handleServerResponse(
						parsedState,
						lockToken
					)
					publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' })
				})
				.catch(() => {
					cartDebug('cart-items:optimistic-qty:exception')
					releaseLock(lockToken)
					void (
						cartDrawerWrapper as CartDrawer
					).forceRefreshFromServer()
				})
			return
		}

		// ── Legacy path (cart page or no CartState) ──────────────────
		// Optimistic remove: collapse the line item immediately
		if (quantity === '0') {
			const lineItem = document.querySelector(
				`${this.instanceSelectors.line}-${line}`
			)
			if (lineItem instanceof HTMLElement) {
				lineItem.style.maxHeight = `${lineItem.scrollHeight}px`
				requestAnimationFrame(() => {
					lineItem.classList.add('cart-item--removing')
				})
			}
		}

		this.enableLoading(line)

		// Capture item data before removal for Segment tracking
		if (quantity === '0') {
			// Fire-and-forget: get cart data to capture the item being removed
			void this.captureAndTrackRemoval(parseInt(line))
		}

		const body = JSON.stringify({
			line: parseInt(line),
			quantity: parseInt(quantity),
			sections: this.getSectionsToRender().map(
				(section) => section.section
			),
			sections_url: window.routes.cart_url,
		})

		cartDebug('cart-items:legacy-request', {
			line,
			quantity,
			body: JSON.parse(body),
		})

		fetch(`${window.routes.cart_change_url}`, {
			...fetchConfig(),
			...{ body },
		})
			.then((response) => {
				return response.text()
			})
			.then((state) => {
				const parsedState = JSON.parse(state)
				cartDebug('cart-items:legacy-response', {
					hasErrors: Boolean(parsedState.errors),
					itemCount: parsedState.item_count,
					items: summarizeCartItems(parsedState.items ?? []),
				})
				// same as cart add, check for update instructions in case we need to remove gwp
				const cartUpdateInstructions =
					getUpdateInstructions(parsedState)
				cartDebug('cart-items:legacy-instructions', {
					instructions: summarizeInstructions(cartUpdateInstructions),
				})
				// When manually removing a GWP/bundle line, skip additions for this pass
				if (window.__skipGwpAdditions) {
					cartUpdateInstructions.additions = []
					cartUpdateInstructions.update_required =
						Object.keys(cartUpdateInstructions.updates).length > 0
				}
				if (cartUpdateInstructions.update_required) {
					const cartDrawer = q.rs<CartDrawer>('cart-drawer')
					void cartDrawer.runUpdateInstructions(
						cartUpdateInstructions
					)
					// skip the re-render since we're about to rerender anyway
					return
				}
				window.__skipGwpAdditions = false
				// continue with normal cart update
				const quantityElement = q.os<HTMLInputElement>(
					`${this.instanceSelectors.lineQuantity}-${line}`
				)
				const items = document.querySelectorAll(
					this.instanceSelectors.item
				)

				if (parsedState.errors) {
					if (quantityElement) {
						quantityElement.value = q.ra(quantityElement, 'value')
					}
					this.updateLiveRegions(line, parsedState.errors)
					return
				}

				const cartDrawerEl = q.os<CartDrawer>('cart-drawer')
				cartDrawerEl?.updateMembershipNotice(parsedState)
				const cartFooter = q.os(this.instanceSelectors.footer)
				if (parsedState.item_count === 0) {
					this.setAttribute(ATTRIBUTES.cartEmpty, '')
					cartFooter &&
						cartFooter.setAttribute(ATTRIBUTES.cartEmpty, '')
					cartDrawerEl &&
						cartDrawerEl.setAttribute(ATTRIBUTES.cartEmpty, '')
				} else {
					this.removeAttribute(ATTRIBUTES.cartEmpty)
					cartFooter &&
						cartFooter.removeAttribute(ATTRIBUTES.cartEmpty)
					cartDrawerEl &&
						cartDrawerEl.removeAttribute(ATTRIBUTES.cartEmpty)
				}

				this.getSectionsToRender().forEach(
					(section: ShopifySectionRenderingSchema) => {
						if (section.section === 'cart-update-instructions')
							return
						if (section.section === 'dynamic-progress-bar') {
							updateProgressBar(
								parsedState.sections[section.section]
							)
							return
						}
						const sectionEl = q.rs(`#${section.id}`)
						const elementToReplace =
							(section.selector &&
								sectionEl.querySelector(section.selector)) ||
							sectionEl
						if (!section.section)
							throw new Error('no section.section')
						if (!section.selector)
							throw new Error('no section.selector')
						elementToReplace.innerHTML = this.getSectionInnerHTML(
							parsedState.sections[section.section],
							section.selector
						)
					}
				)
				const updatedValue = parsedState.items[parseInt(line) - 1]
					? parsedState.items[parseInt(line) - 1].quantity
					: undefined
				let message = ''
				if (
					items.length === parsedState.items.length &&
					quantityElement &&
					updatedValue !== parseInt(quantityElement.value)
				) {
					if (typeof updatedValue === 'undefined') {
						message = window.cartStrings.error
					} else {
						message = window.cartStrings.quantityError.replace(
							'[quantity]',
							updatedValue
						)
					}
				}
				this.updateLiveRegions(line, message)

				const lineItem = q.os(`${this.instanceSelectors.line}-${line}`)
				if (lineItem?.querySelector(`[name="${name}"]`)) {
					cartDrawerEl
						? window.TsDOM.trapFocus(
								cartDrawerEl,
								q.rs(`[name="${name}"]`, lineItem)
							)
						: q.rs(`[name="${name}"]`, lineItem).focus()
				} else if (parsedState.item_count === 0 && cartDrawerEl) {
					const cartEmptyEl = q.os(
						this.instanceSelectors.cartDrawerInnerEmpty,
						cartDrawerEl
					)
					if (cartEmptyEl) {
						window.TsDOM.trapFocus(
							cartEmptyEl,
							q.rs('a', cartDrawerEl)
						)
					}
				} else if (
					document.querySelector(this.instanceSelectors.item) &&
					cartDrawerEl
				) {
					const firstItemLink = q.os(this.instanceSelectors.itemLink)
					if (firstItemLink) {
						window.TsDOM.trapFocus(cartDrawerEl, firstItemLink)
					}
				}
				void window.Ucoast.mediaManager.playAllInContainer(this)
				publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' })
				this.disableLoading(line)
			})
			.catch(() => {
				cartDebug('cart-items:legacy-exception', {
					line,
					quantity,
				})
				this.querySelectorAll(
					this.instanceSelectors.loadingOverlay
				).forEach((overlay) => overlay.classList.add('hidden'))
				const errors = q.rs(this.instanceSelectors.errors)
				errors.textContent = window.cartStrings.error
				this.disableLoading(line)
				// Force refresh to restore optimistically-hidden items
				if (isDrawer) {
					void renderResponseToCartDrawer({ items: [] })
				} else {
					this.onCartUpdate()
				}
			})
	}

	updateLiveRegions(line: string, message: string) {
		const lineItemError = q.os(
			`${this.instanceSelectors.lineError}-${line}`
		)
		if (lineItemError) {
			const errorText = q.rs(
				this.instanceSelectors.itemErrorText,
				lineItemError
			)
			errorText.innerHTML = message
		}

		this.lineItemStatusElement.setAttribute('aria-hidden', 'true')

		const cartStatus = q.rs(this.instanceSelectors.liveRegionText)
		cartStatus.setAttribute('aria-hidden', 'false')

		setTimeout(() => {
			cartStatus.setAttribute('aria-hidden', 'true')
		}, 1000)
	}

	getSectionInnerHTML(html: string, selector: string) {
		const newDocument = new DOMParser().parseFromString(html, 'text/html')
		const newElement = q.rs(selector, newDocument.documentElement)
		return newElement.innerHTML
	}

	enableLoading(line: string) {
		const mainCartItems = this.getMainCartItems()
		mainCartItems.setAttribute(CartItems.attributes.disabled, '')

		this.getCartItemElements(line)?.forEach((overlay) =>
			overlay.classList.remove('hidden')
		)

		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur()
		}

		this.lineItemStatusElement.setAttribute('aria-hidden', 'false')
	}

	getMainCartItems() {
		return q.rs(this.instanceSelectors.main)
	}

	getCartItemElements(line: string) {
		return q.ol(
			`${this.instanceSelectors.line}-${line} ${this.instanceSelectors.loadingOverlay}`,
			this
		)
	}

	disableLoading(line: string) {
		this.getMainCartItems().removeAttribute(CartItems.attributes.disabled)
		this.getCartItemElements(line)?.forEach((overlay) =>
			overlay.classList.add('hidden')
		)
	}

	/**
	 * Capture item data from current cart and track Product Removed / GWP Item Removed
	 */
	private async captureAndTrackRemoval(lineIndex: number) {
		try {
			if (!window.SegmentUser) return

			const cart = await getCart()
			if ('status' in cart) return

			// Cart line indices are 1-based
			const item = cart.items[lineIndex - 1]
			if (!item) return

			// Check if this is a GWP item
			if (item.properties?._gwp_item || item.properties?._gwp_rule_id) {
				void window.SegmentUser.sendSegmentEvent(null, {
					eventName: 'GWP Item Removed',
					product_title: item.product_title,
					gwp_rule_id: item.properties._gwp_rule_id || 'legacy',
					removal_method: 'direct_deletion',
					sent_from: 'Pvolve Web Script',
				})
			}

			// Track Product Removed for all items (including GWP)
			void window.SegmentUser.sendSegmentEvent(null, {
				eventName: 'Product Removed',
				sent_from: 'Pvolve Web Script',
				brand: item.vendor,
				category: item.product_type,
				url: window.location.origin + '/products/' + item.handle,
				product_id: item.product_id,
				sku: item.sku,
				name: item.product_title,
				price: item.price / 100,
				variant: item.variant_title || '',
				shopify_product_id: item.product_id,
				shopify_variant_id: item.id,
				currency: 'USD',
				image_url: item.image,
			})
		} catch (e) {
			console.error('[Segment] Error tracking Product Removed:', e)
		}
	}
}
